import { useFormContext } from "react-hook-form";
import { faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext, useEffect, useRef, useState } from "react";
import FontFaceObserver from "fontfaceobserver";

import MyTextField from "../MyTextField";
import MyButton from "../../MyButton";
import { useCaptcha } from "../../../hooks/useCaptcha";
import { api } from "../../../api";
import { APIs } from "../../../api/APIs";
import { AuthContext } from "../../../context/AuthProvider";
import { SiteContext } from "../../../context/SiteProvider";

const MyInputCaptcha = (props) => {
  const { className, labelClass = "md:w-[240px]", name } = props;

  const { useGetCaptchaId, verifyCaptchaMutation } = useCaptcha();
  const { isAuthenticated } = useContext(AuthContext);
  const { setLoadingScreen } = useContext(SiteContext);

  const { setValue, setError, getValues, clearErrors, trigger, watch } =
    useFormContext();

  const canvasRef = useRef(null);
  const timer = useRef(null);

  const [isFontLoaded, setIsFontLoaded] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isCaptchaLoading, setIsCaptchaLoading] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(0);

  const valid = watch(name);

  const {
    data: dataCaptcha,
    isLoading,
    refetch,
  } = useGetCaptchaId(
    {
      enabled: isReady,
    },
    (data) => data.data
  );

  const drawCaptcha = async () => {
    if (!isFontLoaded) return;

    setIsCaptchaLoading(true);

    try {
      const captchaId = getValues("captchaId");
      const api_info = APIs.captcha.get_text(captchaId);
      const response = await api(api_info);

      if (response?.data?.captchaValue) {
        setValue("sampleCaptcha", response.data.captchaValue);

        if (!isAuthenticated) {
          const canvas = canvasRef.current;
          const ctx = canvas.getContext("2d");

          canvas.width = 180;
          canvas.height = 56;

          ctx.fillStyle = "#f0f0f0";
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          ctx.font = "italic 36px 'LoveYaLikeASister', sans-serif";
          ctx.fillStyle = "#505050";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(
            response.data.captchaValue,
            canvas.width / 2,
            canvas.height / 2
          );

          for (let i = 0; i < 50; i++) {
            ctx.strokeStyle = `rgba(${Math.random() * 255}, ${
              Math.random() * 255
            }, ${Math.random() * 255}, 0.5)`;
            ctx.beginPath();
            ctx.moveTo(
              Math.random() * canvas.width,
              Math.random() * canvas.height
            );
            ctx.lineTo(
              Math.random() * canvas.width,
              Math.random() * canvas.height
            );
            ctx.stroke();
          }
        } else {
          setValue("captcha", response.data.captchaValue);
          await onVerifyCaptcha(false);
        }
      }
    } catch (error) {
    } finally {
      setIsCaptchaLoading(false);
    }
  };

  const handleRefreshCaptcha = async () => {
    if (isCaptchaLoading) {
      alert("너무 빨리 조작하고 있습니다.");
      return;
    }

    const currentTime = Date.now();

    if (currentTime - lastFetchTime < 5000) {
      alert("너무 빨리 조작하고 있습니다.");
      return;
    }

    setLastFetchTime(currentTime);

    clearErrors("captcha");
    setValue("captcha", "");
    setValue(name, "");

    refetch();
  };

  const onVerifyCaptcha = async (showAlert = true) => {
    const isValidCaptcha = await trigger("captcha");

    if (isValidCaptcha) {
      const captcha = getValues("captcha");
      const sampleCaptcha = getValues("sampleCaptcha");

      if (captcha !== sampleCaptcha) {
        setError("captcha", {
          message: "인증에 실패했습니다. 다시 확인해주세요",
        });
        return;
      }

      if (!isAuthenticated) setLoadingScreen(true);

      const payload = {
        captchaId: getValues("captchaId"),
        captchaValue: captcha,
      };
      verifyCaptchaMutation.mutate(payload, {
        onSuccess: (res) => {
          if (res?.captchaToken) {
            clearErrors("captcha");
            setValue(name, res.captchaToken);
          } else {
            showAlert && alert("인증에 실패했습니다. 다시 확인해주세요");
          }
        },
        onError: () => {
          showAlert && alert("인증에 실패했습니다. 다시 확인해주세요");
        },
        onSettled: () => {
          setLoadingScreen(false);
        },
      });
    }
  };

  useEffect(() => {
    const font = new FontFaceObserver("LoveYaLikeASister");

    font.load().then(() => {
      setIsFontLoaded(true);

      if (!timer.current) {
        timer.current = setTimeout(() => {
          setIsReady(true);
        }, 2000);
      }
    });

    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFontLoaded && dataCaptcha && !isLoading) {
      setValue("captchaId", dataCaptcha.captchaId);
      drawCaptcha();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, dataCaptcha, isFontLoaded]);

  return (
    <div className={className}>
      <div className="flex flex-wrap md:mb-10 mb-5">
        <label
          htmlFor="captcha"
          className={`${labelClass} w-full md:mt-4 font-semibold`}
        >
          자동입력방지 인증 <span className="text-error">*</span>
        </label>

        <div className="flex-1">
          <div className="flex flex-wrap items-stretch gap-1">
            <div className="h-14 w-[180px]">
              <canvas ref={canvasRef} />
            </div>
            <MyButton
              variant="outlined"
              type="button"
              className="w-8 rounded hover:bg-[#F0F0F0] mr-2"
              style={{ height: 56 }}
              onClick={handleRefreshCaptcha}
            >
              <FontAwesomeIcon
                icon={faRotateRight}
                className="text-gray-dark"
              />
            </MyButton>
            <div className="flex-1 xl:max-w-[255px] min-w-[230px] mr-2">
              <MyTextField
                id="captcha"
                name="captcha"
                placeholder="자동등록 방지 코드를 입력하세요."
                onChange={() => setValue(name, "")}
              />
              {valid && (
                <p className="text-sm text-success">인증이 완료되었습니다.</p>
              )}
            </div>
            <MyButton
              className="md:w-[120px] xs:w-max w-full"
              variant="outlined"
              style={{ height: 56 }}
              onClick={onVerifyCaptcha}
            >
              <span className="text-gray-dark text-sm font-semibold">
                인증하기
              </span>
            </MyButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyInputCaptcha;
